import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { vehiclesAPI } from '@/lib/api';
import { formatCurrency, formatDate } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { 
  ArrowLeft, 
  Car, 
  User,
  Phone,
  Gauge,
  Calendar,
  FileText,
  Plus
} from 'lucide-react';
import { toast } from 'sonner';

export default function VehicleDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [vehicle, setVehicle] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [vehicleRes, historyRes] = await Promise.all([
          vehiclesAPI.get(id),
          vehiclesAPI.getHistory(id)
        ]);
        setVehicle(vehicleRes.data);
        setHistory(historyRes.data);
      } catch (error) {
        toast.error('Failed to load vehicle details');
        navigate('/vehicles');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, navigate]);

  const getStatusBadge = (status) => {
    switch (status) {
      case 'paid':
        return <Badge className="badge-success">Paid</Badge>;
      case 'pending':
        return <Badge className="badge-warning">Pending</Badge>;
      case 'partial':
        return <Badge className="badge-info">Partial</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="spinner" />
      </div>
    );
  }

  if (!vehicle) {
    return (
      <div className="text-center py-12">
        <p>Vehicle not found</p>
        <Button onClick={() => navigate('/vehicles')} className="mt-4">
          Back to Vehicles
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="vehicle-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/vehicles')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-black font-heading tracking-tight font-mono">
              {vehicle.vehicle_number}
            </h1>
            <p className="text-muted-foreground">
              {vehicle.make} {vehicle.model} {vehicle.year && `(${vehicle.year})`}
            </p>
          </div>
        </div>
        <Link to={`/invoices/new?vehicle=${vehicle.id}`}>
          <Button className="font-semibold uppercase tracking-wide" data-testid="new-invoice-btn">
            <Plus className="w-4 h-4 mr-2" />
            New Invoice
          </Button>
        </Link>
      </div>

      {/* Vehicle & Customer Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Vehicle Details */}
        <Card data-testid="vehicle-info-card">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Car className="w-5 h-5" />
              Vehicle Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Vehicle Number</p>
                <p className="font-mono font-bold text-lg">{vehicle.vehicle_number}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Make & Model</p>
                <p className="font-medium">{vehicle.make || '-'} {vehicle.model || ''}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Year</p>
                <p className="font-medium">{vehicle.year || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Color</p>
                <p className="font-medium">{vehicle.color || '-'}</p>
              </div>
              {vehicle.meter_reading && (
                <div>
                  <p className="text-sm text-muted-foreground">Meter Reading</p>
                  <p className="font-medium flex items-center gap-1">
                    <Gauge className="w-4 h-4" />
                    {vehicle.meter_reading.toLocaleString()} km
                  </p>
                </div>
              )}
              {vehicle.chassis_number && (
                <div className="col-span-2">
                  <p className="text-sm text-muted-foreground">Chassis Number</p>
                  <p className="font-mono text-sm">{vehicle.chassis_number}</p>
                </div>
              )}
              {vehicle.engine_type && (
                <div>
                  <p className="text-sm text-muted-foreground">Engine Type</p>
                  <p className="font-medium">{vehicle.engine_type}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Customer Details */}
        <Card data-testid="customer-info-card">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <User className="w-5 h-5" />
              Owner Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {vehicle.customer ? (
              <>
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                    <User className="w-8 h-8 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-lg">{vehicle.customer.name}</p>
                    <div className="flex items-center gap-1 text-muted-foreground">
                      <Phone className="w-4 h-4" />
                      {vehicle.customer.phone}
                    </div>
                  </div>
                </div>
                <Link to={`/customers/${vehicle.customer_id}`}>
                  <Button variant="outline" className="w-full" data-testid="view-customer-btn">
                    View Customer Profile
                  </Button>
                </Link>
              </>
            ) : (
              <p className="text-muted-foreground">No owner information</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Service History */}
      <Card data-testid="service-history-card">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Service History
          </CardTitle>
        </CardHeader>
        <CardContent>
          {history.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Invoice #</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-24">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {history.map((invoice) => (
                    <TableRow key={invoice.id} data-testid={`history-row-${invoice.id}`}>
                      <TableCell className="font-mono font-bold">
                        {invoice.invoice_no}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Calendar className="w-4 h-4 text-muted-foreground" />
                          {formatDate(invoice.date)}
                        </div>
                      </TableCell>
                      <TableCell>{invoice.customer_name}</TableCell>
                      <TableCell className="text-right font-mono font-bold">
                        {formatCurrency(invoice.grand_total)}
                      </TableCell>
                      <TableCell>{getStatusBadge(invoice.payment_status)}</TableCell>
                      <TableCell>
                        <Link to={`/invoices/${invoice.id}`}>
                          <Button variant="ghost" size="sm" data-testid={`view-invoice-${invoice.id}`}>
                            View
                          </Button>
                        </Link>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-12">
              <FileText className="w-16 h-16 mx-auto text-muted-foreground/30" />
              <p className="mt-4 text-muted-foreground">No service history found</p>
              <p className="text-sm text-muted-foreground">Create an invoice to see service records here</p>
              <Link to={`/invoices/new?vehicle=${vehicle.id}`}>
                <Button className="mt-4" data-testid="create-first-invoice-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Create First Invoice
                </Button>
              </Link>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Summary Stats */}
      {history.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-6">
              <p className="text-sm text-muted-foreground">Total Invoices</p>
              <p className="text-3xl font-bold font-heading">{history.length}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <p className="text-sm text-muted-foreground">Total Spent</p>
              <p className="text-3xl font-bold font-heading font-mono">
                {formatCurrency(history.reduce((sum, inv) => sum + (inv.grand_total || 0), 0))}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <p className="text-sm text-muted-foreground">Last Service</p>
              <p className="text-xl font-bold font-heading">
                {formatDate(history[0]?.date)}
              </p>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
